import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import DeleteDivergentEmergencyAlarmSetting from 'features/emergency-alarm-settings/domain/models/delete-divergent-emergency-alarm-setting';
import EmergencyAlarmSetting from '../domain/models/emergency-alarm-setting';
import ReadEmergencyAlarmSettingsResponse from '../domain/models/read-emergency-alarm-settings-response';

const baseUrl = '/api/v1/Settings';

enum EmergencyAlarmSettingsQueryPaths {
  ReadEmergencyAlarmSettings = 'ReadEmergencyAlarmSettings',
  UpsertEmergencyAlarmSetting = 'UpsertEmergencyAlarmSetting',
  DeleteEmergencyAlarmSetting = 'DeleteDivergentEmergencyAlarmSetting',
}

const readEmergencyAlarmSettings = async (axiosInstance: AxiosInstance): Promise<ReadEmergencyAlarmSettingsResponse> => {
  const response = await axiosInstance.get(`${baseUrl}/${EmergencyAlarmSettingsQueryPaths.ReadEmergencyAlarmSettings}`);
  return response.data;
};

const upsertEmergencyAlarmSetting = async (axiosInstance: AxiosInstance, body: EmergencyAlarmSetting): Promise<EmergencyAlarmSetting> => {
  const response = await axiosInstance.put(`${baseUrl}/${EmergencyAlarmSettingsQueryPaths.UpsertEmergencyAlarmSetting}`, body);
  return response.data;
};

const deleteEmergencyAlarmSetting = async (axiosInstance: AxiosInstance, id: string): Promise<DeleteDivergentEmergencyAlarmSetting> => {
  const response = await axiosInstance.delete(`${baseUrl}/${EmergencyAlarmSettingsQueryPaths.DeleteEmergencyAlarmSetting}/${id}`);
  return response.data;
};

export const useReadEmergencyAlarmSettings = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [EmergencyAlarmSettingsQueryPaths.ReadEmergencyAlarmSettings],
    queryFn: () => readEmergencyAlarmSettings(authenticatedInstance),
  });
};

export const useUpsertEmergencyAlarmSetting = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [EmergencyAlarmSettingsQueryPaths.UpsertEmergencyAlarmSetting],
    mutationFn: (body: EmergencyAlarmSetting) => upsertEmergencyAlarmSetting(authenticatedInstance, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EmergencyAlarmSettingsQueryPaths.ReadEmergencyAlarmSettings],
      });
    },
  });
};

export const useDeleteEmergencyAlarmSetting = () => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [EmergencyAlarmSettingsQueryPaths.DeleteEmergencyAlarmSetting],
    mutationFn: (id: string) => deleteEmergencyAlarmSetting(authenticatedInstance, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [EmergencyAlarmSettingsQueryPaths.ReadEmergencyAlarmSettings],
      });
    },
  });
};
